import { computed, ref } from 'vue'
import { isEqual, uniqBy } from 'lodash-es'

import useCanvas from '@canvas-builder/composables/useCanvas'
import useCanvasSectionOverrides from '@canvas-builder/composables/useCanvasSectionOverrides'
import { collectAllNodesByCondition } from '@canvas-builder/util/tree'
import { urlSafeFetchInChunks } from '@lib/util/network'
import { indirectEval } from '@lib/util/eval'
import {
  ComponentTypes,
  type ContentGridProps,
  type ContentGridItemFileProps,
  type ContentGridItemSlideProps,
  type CanvasBuilderProps,
  type ComponentNode,
} from '@canvas-builder/types/canvas'
import type { Recommendations, RecommendedContent } from '@canvas-builder/types/recommendations.types'
import { type CanvasRetrieve, type File, type FileContentTypeEnum, type CanvasRecommendedFile } from '@openapi'
import type { InstanceSettings } from '@lib/types/instanceSettings.types'

const logSentry = ({ message, error, extra }: { message?: string; error?: Error; extra?: Record<string, any> }) => {
  if (!window.Sentry) {
    console.warn('Sentry is not available', { message, error, extra })
  } else {
    console.info('Logging to Sentry', { message, error, extra })
    window.Sentry.withScope((scope: any) => {
      scope.setExtra('file', 'useRecommendations.ts')
      extra && scope.setExtras(extra)
      error && window.Sentry.captureException(error)
      message && window.Sentry.captureMessage(message)
    })
  }
}

const recommendationStrategy = computed(
  () =>
    useCanvas().activeCanvas.value?.context?.pitcher
      ?.recommendation_strategy as Recommendations['recommendation_strategy'],
)
const contextSections = computed(() =>
  (
    (useCanvas().activeCanvas.value?.context?.pitcher?.sections ?? []) as NonNullable<Recommendations['sections']>
  ).filter((rs) => rs?.id),
)
const recommendedSectionIds = computed<CanvasRetrieve['id'][]>(() =>
  contextSections.value.filter((s) => s.recommended).map((s) => s.id),
)

const listHeaderHtmlBySectionId = computed(() =>
  contextSections.value.reduce(
    (acc, section) => {
      acc[section.id] = section.list_header_html ?? ''
      return acc
    },
    {} as Record<CanvasRetrieve['id'], string>,
  ),
)

const recommendedContent = computed(
  () =>
    (useCanvas().activeCanvas.value?.context?.pitcher?.recommended_content ?? {}) as NonNullable<
      Recommendations['recommended_content']
    >,
)

const nameComparator = (
  a: ContentGridItemSlideProps | ContentGridItemFileProps,
  b: ContentGridItemSlideProps | ContentGridItemFileProps,
) => (a.file?.name ?? '').localeCompare(b.file?.name ?? '')

const prepareContentGridNode = (
  contentRecommendation: RecommendedContent[number],
  filesById: Record<File['id'], File>,
): ContentGridProps['items'][number] => {
  const file = filesById[contentRecommendation.file?.id]
  if (contentRecommendation.type === 'file') {
    return {
      file: {
        id: contentRecommendation.file.id,
        name: file?.name,
        thumbnail_url: file?.thumbnail_url,
        content_type: file?.content_type,
      },
      type: contentRecommendation.type,
    } as ContentGridItemFileProps
  }

  return {
    file: {
      id: contentRecommendation.file.id,
      name: file?.name,
    },
    slide: {
      index: contentRecommendation.slide?.index ?? 0,
    },
    type: 'slide',
  }
}

const fetchFilesByStrategy = async (
  strategies: MatchingStrategy[],
  sectionId: CanvasRetrieve['id'],
  options: {
    recommendedFilesFetcher: NonNullable<CanvasBuilderProps['listRecommendedFiles']>
  },
): Promise<Partial<Record<MatchingStrategy, CanvasRecommendedFile[]>>> => {
  const filesByStrategy: Partial<Record<MatchingStrategy, CanvasRecommendedFile[]>> = {}

  for (const strategy of strategies) {
    if (strategy === 'matching_metadata') {
      filesByStrategy[strategy] = await options.recommendedFilesFetcher({ sectionId, matchingStrategy: ['metadata'] })
    } else if (strategy === 'matching_tags') {
      filesByStrategy[strategy] = await options.recommendedFilesFetcher({ sectionId, matchingStrategy: ['tags'] })
    } else if (strategy === 'matching_metadata_or_tags') {
      filesByStrategy[strategy] = await options.recommendedFilesFetcher({
        sectionId,
        matchingStrategy: ['metadata', 'tags'],
      })
    }
  }

  return filesByStrategy
}

async function autofillCanvasContentGridsWithAi(filesFetcher: NonNullable<CanvasBuilderProps['listFiles']>) {
  const { canvasContent, patchCanvasContentByCondition, activeCanvas } = useCanvas()
  const logInfo = (msg: string) => console.info(`[autofillCanvasContentGridsWithAi]: ${msg}`)

  try {
    if (recommendationStrategy.value !== 'force') {
      logInfo('Recommendation strategy is not force. Bailing...')
      return
    } else if (!activeCanvas.value?.id) {
      logSentry({
        error: new Error('No active canvas. This should not happen...'),
        extra: { activeCanvas: activeCanvas.value },
      })
      return
    } else if (
      !collectAllNodesByCondition(
        canvasContent.value ?? [],
        (n) => n.type === ComponentTypes.ContentGrid && n?.autofill === 'ai',
      ).length
    ) {
      logInfo('No AI driven content grids to autofill. Bailing...')
      return
    }

    const contentGrids = recommendedContent.value.content_grids ?? []
    const contentGridsFileIds = uniqBy(contentGrids, (s) => s.file.id).map(({ file }) => file.id)
    if (!contentGridsFileIds.length) {
      logInfo('No recommended file ids from AI. Bailing...')
      return
    }
    const files = await urlSafeFetchInChunks(contentGridsFileIds, (chunk) =>
      filesFetcher?.({ filters: { id__in: chunk.join(',') } }),
    )
    const filesById = files.reduce((acc, f) => ({ ...acc, [f.id]: f }), {})
    const items = contentGrids.map((n) => prepareContentGridNode(n, filesById)).sort(nameComparator)

    patchCanvasContentByCondition((n: ComponentNode) => n.type === ComponentTypes.ContentGrid && n?.autofill === 'ai', {
      data: { items },
    })
    logInfo('Autofilled content grids with AI. Dispatching update...')
  } catch (error) {
    console.error(`Error autofilling content grids with AI for canvas ${activeCanvas.value?.id}`, error)
  }
}

async function autofillSectionContentGridsWithAi(filesFetcher: NonNullable<CanvasBuilderProps['listFiles']>) {
  try {
    const { activeCanvas, sectionComponentOverrides } = useCanvas()
    const { overrideSectionComponentData } = useCanvasSectionOverrides()
    if (recommendationStrategy.value !== 'force') {
      console.info('[autofillSectionContentGridsWithAi]: Recommendation strategy is not force. Bailing...')
      return
    } else if (!activeCanvas.value?.id) {
      logSentry({
        error: new Error('No active canvas. This should not happen...'),
        extra: { activeCanvas: activeCanvas.value },
      })
      return
    }

    // Collect all file IDs from all sections first
    const allContentGridsFileIds =
      activeCanvas.value?.sections
        ?.flatMap((s) => recommendedContent.value.sections?.[s.id]?.content_grids ?? [])
        .map((cg) => cg.file?.id) ?? []

    const uniqueFileIds = uniqBy(allContentGridsFileIds, (id) => id)

    // Fetch all files at once
    let filesById = {}
    if (uniqueFileIds.length > 0) {
      const files = await urlSafeFetchInChunks(uniqueFileIds, (chunk) =>
        filesFetcher?.({ filters: { id__in: chunk.join(',') } }),
      )
      filesById = files.reduce((acc, f) => ({ ...acc, [f.id]: f }), {})
    } else {
      return
    }

    // Process each section
    if (activeCanvas.value?.sections) {
      for (const s of activeCanvas.value.sections) {
        try {
          const nodesToAutofill = collectAllNodesByCondition(
            s.content?.data ?? [],
            (n: ComponentNode) =>
              n.type === ComponentTypes.ContentGrid &&
              n?.autofill === 'ai' &&
              !(sectionComponentOverrides.value[s.id]?.[n.id]?.node as ComponentNode<ContentGridProps> | undefined)
                ?.data?.items.length,
          ) as ComponentNode<ContentGridProps>[]

          if (!nodesToAutofill?.length) {
            console.info(
              `No AI driven content grids to autofill or they are already filled for section ${s.id}. Bailing...`,
            )
            continue
          }

          const contentGrids = recommendedContent.value.sections?.[s.id]?.content_grids ?? []
          const items = contentGrids.map((n) => prepareContentGridNode(n, filesById)).sort(nameComparator)

          nodesToAutofill.forEach((n) => {
            overrideSectionComponentData({
              usedInSectionId: s.id,
              componentId: n.id,
              componentData: { ...n.data, items },
            })
          })
        } catch (error) {
          console.error(`Error autofilling content grids with AI for canvas ${activeCanvas.value?.id}`, error)
        }
      }
    }
  } catch (error) {
    console.error(`Error autofilling content grids with AI`, error)
  }
}

type MatchingStrategy = Exclude<ComponentNode['autofill'], 'off' | undefined>

function extractMatchingStrategies(nodes: ComponentNode[]): MatchingStrategy[] {
  return [...new Set(nodes.map((node) => node.autofill))].filter((s): s is MatchingStrategy => !!s && s !== 'off')
}

async function autofillSectionContentGridsWithMatchingProperties(
  sectionId: CanvasRetrieve['id'],
  options: {
    filesFetcher: NonNullable<CanvasBuilderProps['listFiles']>
    recommendedFilesFetcher: NonNullable<CanvasBuilderProps['listRecommendedFiles']>
    instanceSettings: InstanceSettings
  },
) {
  try {
    const logInfo = (msg: string) => console.info(`[autofillSectionContentGridsWithMatchingProperties]: ${msg}`)
    const { activeCanvas, activeCanvasSections } = useCanvas()
    const { overrideSectionComponentData, sectionComponentOverrides } = useCanvasSectionOverrides()
    const { filesFetcher, recommendedFilesFetcher } = options
    if (!activeCanvas.value?.id || !filesFetcher || !recommendedFilesFetcher) {
      logInfo('Missing required info to autofill recommendations. Bailing...')
      return
    }

    const section = activeCanvasSections.value?.[sectionId]
    if (section) {
      const nodesToAutofill = collectAllNodesByCondition(
        section.content?.data ?? [],
        (n: ComponentNode) =>
          n.type === ComponentTypes.ContentGrid &&
          ['matching_metadata', 'matching_tags', 'matching_metadata_or_tags'].includes(n?.autofill ?? ''),
      ) as ComponentNode<ContentGridProps>[]

      if (!nodesToAutofill.length) {
        logInfo('No content grids to autofill. Bailing...')
        return
      }

      const strategies = extractMatchingStrategies(nodesToAutofill)
      const filesByStrategy = await fetchFilesByStrategy(strategies, sectionId, { recommendedFilesFetcher })

      for (const node of nodesToAutofill) {
        const strategy = node.autofill
        if (!strategy || strategy === 'off') {
          continue
        }
        let files = filesByStrategy[strategy]

        if (!files || files.length === 0) {
          logInfo(`No recommended files from ${strategy}. Bailing...`)
          continue
        }

        const iteratee = options?.instanceSettings?.canvas?.section_content_grid_autofill_files_filter_iteratee
          ? indirectEval(options?.instanceSettings.canvas.section_content_grid_autofill_files_filter_iteratee)
          : undefined

        if (iteratee && typeof iteratee === 'function') {
          files = files.filter((file) => iteratee({ canvas: activeCanvas.value, file, node, section }))
        }

        const filesById = files.reduce((acc, f) => ({ ...acc, [f.id]: f }), {})

        let items = files
          .map((file) => prepareContentGridNode({ file: { id: file.id }, type: 'file' }, filesById))
          .sort(nameComparator)

        if (node.autofill_content_types && node.autofill_content_types.length > 0) {
          items = items.filter((item) =>
            node.autofill_content_types?.includes(item.file.content_type as FileContentTypeEnum),
          )
        }

        if (
          isEqual(
            items,
            (sectionComponentOverrides.value?.[sectionId]?.[node.id]?.node?.data as ContentGridProps | undefined)
              ?.items,
          )
        ) {
          logInfo('Section grid recommendations exactly the same. Moving on...')
          continue
        } else {
          // DEBUG: check recommendations diff
          // const existingItems =
          //   (sectionComponentOverrides.value?.[sectionId]?.[node.id]?.node?.data as ContentGridProps | undefined)
          //     ?.items || []
          // const diff = differenceWith(items, existingItems, isEqual)
          // logInfo('Section grid recommendations are different. Overriding...', { diff })
        }

        overrideSectionComponentData({
          usedInSectionId: sectionId,
          componentId: node.id,
          componentData: {
            ...node.data,
            items,
          },
        })
      }
    }
  } catch (e) {
    logSentry({ error: e as Error, extra: { sectionId } })
  }
}

const standaloneSectionContentGridsWithMatchingPropertiesPreviewData = ref<
  Record<ComponentNode['id'], ContentGridProps>
>({})
const previewStandaloneSectionContentGridsWithMatchingProperties = async (options: {
  filesFetcher: NonNullable<CanvasBuilderProps['listFiles']>
  recommendedFilesFetcher: NonNullable<CanvasBuilderProps['listRecommendedFiles']>
}) => {
  try {
    standaloneSectionContentGridsWithMatchingPropertiesPreviewData.value = {}
    const { activeCanvas } = useCanvas()
    const { filesFetcher, recommendedFilesFetcher } = options

    if (!activeCanvas.value?.id || !filesFetcher || !recommendedFilesFetcher) {
      console.info('Missing required info to preview recommendations. Bailing...')
      return
    }

    const nodesToPreview = collectAllNodesByCondition(
      activeCanvas.value.content?.data ?? [],
      (n: ComponentNode) =>
        n.type === ComponentTypes.ContentGrid &&
        ['matching_metadata', 'matching_tags', 'matching_metadata_or_tags'].includes(n?.autofill ?? ''),
    ) as ComponentNode<ContentGridProps>[]

    const strategies = extractMatchingStrategies(nodesToPreview)

    const filesByStrategy = await fetchFilesByStrategy(strategies, activeCanvas.value.id, { recommendedFilesFetcher })

    for (const node of nodesToPreview) {
      const strategy = node.autofill
      if (!strategy || strategy === 'off') {
        continue
      }
      const files = filesByStrategy[strategy]

      if (!files || files.length === 0) {
        console.info(`No recommended files from ${strategy}. Bailing...`)
        continue
      }

      const filesById = files.reduce((acc, f) => ({ ...acc, [f.id]: f }), {})
      const items = files
        .map((file) => prepareContentGridNode({ file: { id: file.id }, type: 'file' }, filesById))
        .sort(nameComparator)

      standaloneSectionContentGridsWithMatchingPropertiesPreviewData.value[node.id] = {
        ...node.data,
        ...(node.autofill_content_types && node.autofill_content_types.length > 0
          ? {
              items: items.filter((item) =>
                node.autofill_content_types?.includes(item.file.content_type as FileContentTypeEnum),
              ),
            }
          : { items }),
      }
    }
  } catch (e) {
    logSentry({ error: e as Error })
  }
}

async function autofillCanvasContentGridsWithMatchingProperties(options: {
  filesFetcher: NonNullable<CanvasBuilderProps['listFiles']>
  recommendedFilesFetcher: NonNullable<CanvasBuilderProps['listRecommendedFiles']>
  instanceSettings?: InstanceSettings
}) {
  try {
    const logInfo = (msg: string) => console.info(`[autofillCanvasContentGridsWithMatchingProperties]: ${msg}`)
    const { activeCanvas, patchCanvasContentByCondition, saveCanvasContent } = useCanvas()
    const { filesFetcher, recommendedFilesFetcher, instanceSettings } = options

    if (!activeCanvas.value?.id || !filesFetcher || !recommendedFilesFetcher) {
      logInfo('Missing required info to autofill recommendations. Bailing...')
      return
    }

    const nodesToAutofill = collectAllNodesByCondition(
      activeCanvas.value.content?.data ?? [],
      (n: ComponentNode) =>
        n.type === ComponentTypes.ContentGrid &&
        ['matching_metadata', 'matching_tags', 'matching_metadata_or_tags'].includes(n?.autofill ?? ''),
    ) as ComponentNode<ContentGridProps>[]

    if (!nodesToAutofill.length) {
      logInfo('No content grids to autofill. Bailing...')
      return
    }

    const strategies = extractMatchingStrategies(nodesToAutofill)
    // Bail if activeCanvas metadata or tags are empty based on strategy
    if (
      strategies.includes('matching_metadata') &&
      (!activeCanvas.value.metadata || Object.keys(activeCanvas.value.metadata).length === 0)
    ) {
      logInfo('Active canvas metadata is empty. Bailing...')
      return
    }

    if (strategies.includes('matching_tags') && (!activeCanvas.value.tags || activeCanvas.value.tags.length === 0)) {
      logInfo('Active canvas tags are empty. Bailing...')
      return
    }

    if (
      strategies.includes('matching_metadata_or_tags') &&
      (!activeCanvas.value.metadata || Object.keys(activeCanvas.value.metadata).length === 0) &&
      (!activeCanvas.value.tags || activeCanvas.value.tags.length === 0)
    ) {
      logInfo('Active canvas metadata and tags are empty. Bailing...')
      return
    }

    const filesByStrategy = await fetchFilesByStrategy(strategies, activeCanvas.value.id, { recommendedFilesFetcher })

    for (const node of nodesToAutofill) {
      const strategy = node.autofill
      if (!strategy || strategy === 'off') {
        continue
      }
      let files = filesByStrategy[strategy]

      if (!files || files.length === 0) {
        logInfo(`No recommended files from ${strategy}. Bailing...`)
        continue
      }
      const iteratee = instanceSettings?.canvas?.content_grid_autofill_files_filter_iteratee
        ? indirectEval(instanceSettings.canvas.content_grid_autofill_files_filter_iteratee)
        : undefined

      if (iteratee && typeof iteratee === 'function') {
        files = files.filter((file) => iteratee({ canvas: activeCanvas.value, file, node }))
      }

      const filesById = files.reduce((acc, f) => ({ ...acc, [f.id]: f }), {})
      const items = files
        .map((file) => prepareContentGridNode({ file: { id: file.id }, type: 'file' }, filesById))
        .sort(nameComparator)

      patchCanvasContentByCondition((n: ComponentNode) => n.id === node.id, { data: { ...node.data, items } })
    }
    await saveCanvasContent({ showToast: false })
  } catch (e) {
    logSentry({ error: e as Error })
  }
}

function cleanup() {
  // cleanup any refs on unmount
  standaloneSectionContentGridsWithMatchingPropertiesPreviewData.value = {}
}

export default function useRecommendations() {
  return {
    contextSections,
    listHeaderHtmlBySectionId,
    recommendationStrategy,
    recommendedContent,
    recommendedSectionIds,
    standaloneSectionContentGridsWithMatchingPropertiesPreviewData,
    autofillCanvasContentGridsWithAi,
    autofillCanvasContentGridsWithMatchingProperties,
    autofillSectionContentGridsWithAi,
    autofillSectionContentGridsWithMatchingProperties,
    previewStandaloneSectionContentGridsWithMatchingProperties,
    cleanup,
  }
}
